import {
  CURRENCY_OPTIONS,
  DEFAULT_CURRENCY,
  type Currency,
} from '@/utils/approval/expensePreApproval';
import makeFlowId from '@/utils/approval/makeFlowId';

/** 지출결의서(111) — 레거시 개편판(appr_edit_forms_voucher_outgoing) 구조화 입력을 attributes 로 저장 */
export const EXPENSE_VOUCHER_TYPE = 111;

export const isExpenseVoucherType = (apvType: number): boolean =>
  apvType === EXPENSE_VOUCHER_TYPE;

/** 지급 방법 코드 — 라벨은 번역 key(레거시 apv_lang) */
export const EXPENSE_VOUCHER_PAY_METHODS = [
  { value: 'bank_transfer', label: '계좌이체' },
  { value: 'other_transfer', label: '기타이체' },
  { value: 'overseas_remit', label: '해외송금' },
  { value: 'corporate_card', label: '법인카드' },
  { value: 'offset', label: '상계처리' },
] as const;

export const EXPENSE_VOUCHER_VENDOR_STATUSES = [
  { value: 'normal', label: '정상' },
  { value: 'closed_temp', label: '휴업' },
  { value: 'closed', label: '폐업' },
  { value: 'dissolved', label: '해산·청산' },
  { value: 'bankruptcy', label: '회생·파산' },
  { value: 'trade_stop', label: '거래중지(내부판단)' },
] as const;

export type ExpenseVoucherPayMethod =
  (typeof EXPENSE_VOUCHER_PAY_METHODS)[number]['value'];
export type ExpenseVoucherVendorStatus =
  (typeof EXPENSE_VOUCHER_VENDOR_STATUSES)[number]['value'];

/** 새 작성·다시 열기 때 채워 두는 최소 행 수(기획 합의) */
export const EXPENSE_VOUCHER_BASE_ROWS = 3;

export type ExpenseVoucherRow = {
  id: string;
  category: string;
  summary: string;
  /** 화면 표시값(콤마 포함). 저장 때 콤마를 뗀다 */
  quantity: string;
  currency: Currency;
  amount: string;
  remark: string;
};

export type ExpenseVoucherState = {
  payMethod: ExpenseVoucherPayMethod | '';
  /** YYYY-MM-DD, 미입력이면 '' */
  payRequestDate: string;
  bpVendorNumber: string;
  vendorName: string;
  vendorStatus: ExpenseVoucherVendorStatus | '';
  bankName: string;
  accountNumber: string;
  payee: string;
  confirmAccountMatch: boolean;
  confirmPayBasis: boolean;
  items: ExpenseVoucherRow[];
};

export const createExpenseVoucherRow = (
  init: Partial<Omit<ExpenseVoucherRow, 'id'>> = {},
): ExpenseVoucherRow => ({
  id: makeFlowId('voucher-row'),
  category: '',
  summary: '',
  quantity: '',
  currency: DEFAULT_CURRENCY,
  amount: '',
  remark: '',
  ...init,
});

const padRows = (rows: ExpenseVoucherRow[]): ExpenseVoucherRow[] =>
  rows.length >= EXPENSE_VOUCHER_BASE_ROWS
    ? rows
    : [
        ...rows,
        ...Array.from({ length: EXPENSE_VOUCHER_BASE_ROWS - rows.length }, () =>
          createExpenseVoucherRow(),
        ),
      ];

export const createExpenseVoucherState = (): ExpenseVoucherState => ({
  payMethod: '',
  payRequestDate: '',
  bpVendorNumber: '',
  vendorName: '',
  vendorStatus: '',
  bankName: '',
  accountNumber: '',
  payee: '',
  confirmAccountMatch: false,
  confirmPayBasis: false,
  items: padRows([]),
});

const text = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';

const pick = <T extends string>(
  options: readonly { value: T }[],
  value: unknown,
): T | '' => options.find((o) => o.value === value)?.value ?? '';

export const toCurrency = (value: unknown): Currency => {
  const upper = text(value).trim().toUpperCase();
  return (
    CURRENCY_OPTIONS.find((o) => o.value === upper)?.value ?? DEFAULT_CURRENCY
  );
};

/** 콤마·공백 제거 후 숫자 문자열. 숫자가 아니면 '' (레거시 voucherOutNumber) */
export const normalizeVoucherNumber = (value: string): string => {
  // 입력 중 남은 끝 소수점(1000.)은 정수로 — 빈 값이 되면 금액이 조용히 빠짐
  const v = value.replace(/[,\s]/g, '').replace(/\.$/, '');
  return /^-?[0-9]+(\.[0-9]+)?$/.test(v) ? v : '';
};

/** 천단위 콤마 — 소수부는 받은 그대로 */
export const formatVoucherNumber = (value: string): string => {
  const v = normalizeVoucherNumber(value);
  if (!v) return value;
  const [intPart, decimal] = v.split('.');
  const negative = intPart.startsWith('-');
  const digits = (negative ? intPart.slice(1) : intPart).replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ',',
  );
  return `${negative ? '-' : ''}${digits}${decimal ? `.${decimal}` : ''}`;
};

const formatTotal = (n: number): string =>
  n.toLocaleString('en-US', { maximumFractionDigits: 2 });

/** 개편 이후 문서인지 — 조회 슬롯·상신 검사·재상신이 같은 기준을 씀 */
export const isExpenseVoucherAttributes = (
  attributes: Record<string, unknown> | null | undefined,
): attributes is Record<string, unknown> =>
  !!attributes && 'payMethod' in attributes;

/** 저장 attributes → 작성 상태. 개편 이전 문서(attributes 없음·빈 객체)는 null */
export const parseExpenseVoucherAttributes = (
  attributes: Record<string, unknown> | null | undefined,
): ExpenseVoucherState | null => {
  if (!isExpenseVoucherAttributes(attributes)) return null;
  const rawItems = Array.isArray(attributes.items) ? attributes.items : [];
  const items = rawItems
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
    .map((r) =>
      createExpenseVoucherRow({
        category: text(r.category),
        summary: text(r.summary),
        quantity: formatVoucherNumber(text(r.quantity)),
        currency: toCurrency(r.currency),
        amount: formatVoucherNumber(text(r.amount)),
        remark: text(r.remark),
      }),
    );
  return {
    payMethod: pick(EXPENSE_VOUCHER_PAY_METHODS, attributes.payMethod),
    payRequestDate: text(attributes.payRequestDate),
    bpVendorNumber: text(attributes.bpVendorNumber),
    vendorName: text(attributes.vendorName),
    vendorStatus: pick(
      EXPENSE_VOUCHER_VENDOR_STATUSES,
      attributes.vendorStatus,
    ),
    bankName: text(attributes.bankName),
    accountNumber: text(attributes.accountNumber),
    payee: text(attributes.payee),
    confirmAccountMatch: attributes.confirmAccountMatch === true,
    confirmPayBasis: attributes.confirmPayBasis === true,
    items: padRows(items),
  };
};

const nullIfBlank = (v: string): string | null => (v.trim() ? v.trim() : null);

/** 작성 상태 → 저장 attributes. 빈 행 제거·순번·합계는 서버가 한다 */
export const buildExpenseVoucherAttributes = (
  state: ExpenseVoucherState | null | undefined,
) => {
  if (!state) return null;
  return {
    payMethod: state.payMethod || null,
    payRequestDate: nullIfBlank(state.payRequestDate),
    bpVendorNumber: nullIfBlank(state.bpVendorNumber),
    vendorName: nullIfBlank(state.vendorName),
    vendorStatus: state.vendorStatus || null,
    bankName: nullIfBlank(state.bankName),
    accountNumber: nullIfBlank(state.accountNumber),
    payee: nullIfBlank(state.payee),
    confirmAccountMatch: state.confirmAccountMatch,
    confirmPayBasis: state.confirmPayBasis,
    items: state.items.map((row) => ({
      category: row.category.trim(),
      summary: row.summary.trim(),
      quantity: normalizeVoucherNumber(row.quantity),
      currency: row.currency,
      amount: normalizeVoucherNumber(row.amount),
      remark: row.remark.trim(),
    })),
  };
};

/** 조회 응답 → 재상신 요청. totalAmount·currencyTotals·seq 는 서버 계산값이라 되실으면 422 */
export const pickExpenseVoucherRequestAttributes = (
  attributes: Record<string, unknown> | null | undefined,
) =>
  isExpenseVoucherAttributes(attributes)
    ? buildExpenseVoucherAttributes(parseExpenseVoucherAttributes(attributes))
    : attributes ?? null;

export type CurrencyTotal = { currency: string; total: string };

/** 통화별 합계 — 통화 목록 순서, 금액이 없으면 기본 통화 0 한 줄(레거시 voucherOutRenderTotals) */
export const computeCurrencyTotals = (
  rows: Pick<ExpenseVoucherRow, 'currency' | 'amount'>[],
): CurrencyTotal[] => {
  const sums = new Map<string, number>();
  rows.forEach((row) => {
    const amount = normalizeVoucherNumber(row.amount);
    if (!amount) return;
    sums.set(row.currency, (sums.get(row.currency) ?? 0) + parseFloat(amount));
  });
  const lines = CURRENCY_OPTIONS.filter((o) => sums.has(o.value)).map((o) => ({
    currency: o.value,
    total: formatTotal(Math.round((sums.get(o.value) ?? 0) * 100) / 100),
  }));
  return lines.length > 0
    ? lines
    : [{ currency: DEFAULT_CURRENCY, total: '0' }];
};

/** 조회 응답 currencyTotals({ 통화: 합계 }) → 표시 줄. 없으면 기본 통화 0 한 줄 */
export const currencyTotalsFromAttributes = (
  value: unknown,
): CurrencyTotal[] => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return [{ currency: DEFAULT_CURRENCY, total: '0' }];
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) return [{ currency: DEFAULT_CURRENCY, total: '0' }];
  return entries.map(([currency, total]) => ({
    currency,
    total: formatVoucherNumber(text(total)),
  }));
};

/** 필수 확인 문구 — 번역 key(레거시 APV_VOUT_CONFIRM_*) */
export const EXPENSE_VOUCHER_CONFIRM_ACCOUNT_MATCH_TEXT =
  'BP 거래처번호 · 수취인 · 계좌번호가 지급 요청 내용과 모두 일치함을 확인하였음';
export const EXPENSE_VOUCHER_CONFIRM_PAY_BASIS_TEXT =
  "지급 근거가 되는 관련 품의를 '연결된 품의'에 연결하거나, 관련 파일이 있는 경우 첨부하였음";

export type ExpenseVoucherInvalidField =
  | 'payMethod'
  | 'vendorStatus'
  | 'confirmAccountMatch'
  | 'confirmPayBasis';

export type ExpenseVoucherViolation = {
  message: string;
  fields: ExpenseVoucherInvalidField[];
};

export const EXPENSE_VOUCHER_CONFIRM_MESSAGE =
  '필수 확인 항목을 모두 체크해야 상신할 수 있습니다.';

/** 상신 검사(임시저장은 통과) — 레거시 save_doc_voucher_outgoing 순서·문구 */
export const findExpenseVoucherViolation = (
  state: ExpenseVoucherState | null | undefined,
): ExpenseVoucherViolation | null => {
  if (!state) return null;
  const noPayMethod = !state.payMethod;
  const noVendorStatus = !state.vendorStatus;
  if (noPayMethod && noVendorStatus) {
    return {
      message: '지급 방법 · 거래처 상태를 선택해야 상신할 수 있습니다.',
      fields: ['payMethod', 'vendorStatus'],
    };
  }
  if (noPayMethod) {
    return {
      message: '지급 방법을 선택해야 상신할 수 있습니다.',
      fields: ['payMethod'],
    };
  }
  if (noVendorStatus) {
    return {
      message: '거래처 상태를 선택해야 상신할 수 있습니다.',
      fields: ['vendorStatus'],
    };
  }
  if (!state.confirmAccountMatch || !state.confirmPayBasis) {
    return {
      message: EXPENSE_VOUCHER_CONFIRM_MESSAGE,
      fields: [
        !state.confirmAccountMatch ? 'confirmAccountMatch' : 'confirmPayBasis',
      ],
    };
  }
  return null;
};

export const expenseVoucherKey = (
  state: ExpenseVoucherState | null | undefined,
) => (state ? JSON.stringify(buildExpenseVoucherAttributes(state)) : '');

export const payMethodLabel = (code: unknown): string =>
  EXPENSE_VOUCHER_PAY_METHODS.find((o) => o.value === code)?.label ?? '';

export const vendorStatusLabel = (code: unknown): string =>
  EXPENSE_VOUCHER_VENDOR_STATUSES.find((o) => o.value === code)?.label ?? '';
